import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Plus, Menu, X, ShieldCheck, LayoutDashboard, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite, asset } from "@/context/SiteContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/auctions", label: "المزاد العلني", id: "auctions" },
  { to: "/sale", label: "للبيع", id: "sale" },
  { to: "/rent", label: "للإيجار", id: "rent" },
  { to: "/jobs", label: "وظائف لوجستية", id: "jobs" },
  { to: "/invest", label: "استثمار وشراكة", id: "invest" },
];

export const Logo = () => {
  const s = useSite();
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
      <img src={asset(s.logo)} alt={`${s.site_name_1} ${s.site_name_2}`} className="h-10 w-auto" />
      <div className="leading-tight border-r border-slate-200 pr-2.5">
        <div className="font-display font-extrabold text-base text-slate-900" data-testid="header-site-name">{s.site_name_1} <span className="text-brand">{s.site_name_2}</span></div>
        {s.site_name_en && <div className="text-[10px] tracking-[0.2em] text-slate-400 font-semibold" dir="ltr">{s.site_name_en}</div>}
      </div>
    </Link>
  );
};

const UserArea = ({ onNav }) => {
  const { user, login, logout, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Button data-testid="header-login-btn" onClick={login} variant="outline" className="rounded-lg h-9 border-slate-300 text-slate-800 hover:bg-slate-50">تسجيل الدخول</Button>;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {user.is_admin && (
        <Link to="/admin" onClick={onNav} data-testid="header-admin-link" className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100">
          <ShieldCheck size={16} /> الإدارة
        </Link>
      )}
      <Link to="/dashboard" onClick={onNav} data-testid="header-dashboard-link" className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100">
        {user.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : <LayoutDashboard size={16} />} لوحتي
      </Link>
      <button data-testid="header-logout-btn" onClick={logout} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="خروج"><LogOut size={16} /></button>
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const s = useSite();
  const { user } = useAuth();
  const canAdd = !user || user.is_admin || user.role !== "buyer";
  const linkCls = ({ isActive }) => `px-3.5 py-1.5 rounded-md text-sm font-semibold transition-colors ${isActive ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`;
  const mLinkCls = ({ isActive }) => `block px-3 py-2.5 rounded-lg text-sm font-semibold ${isActive ? "bg-slate-100 text-slate-900" : "text-slate-600"}`;
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200" data-testid="site-header">
      {s.topbar_enabled && s.topbar_text && (
        <div className="bg-slate-900 text-slate-300 text-xs py-2 px-4 text-center font-medium" data-testid="commission-topbar">{s.topbar_text}</div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
          {NAV.map((n) => <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.id}`}>{n.label}</NavLink>)}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          {canAdd && <Link to="/add" data-testid="header-add-listing-btn" className="flex items-center gap-1.5 bg-sun hover:bg-sun-dark text-white rounded-lg px-4 h-9 text-sm font-bold transition-colors shadow-sm"><Plus size={16} />أضف إعلان</Link>}
          <UserArea />
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="lg:hidden border-t bg-white px-4 py-3 space-y-1" data-testid="mobile-menu">
          {NAV.map((n) => <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className={mLinkCls} data-testid={`mobile-nav-${n.id}`}>{n.label}</NavLink>)}
          {canAdd && <Link to="/add" onClick={() => setOpen(false)} data-testid="mobile-add-listing-btn" className="flex items-center gap-1.5 bg-sun text-white rounded-lg px-4 py-2.5 text-sm font-bold w-fit"><Plus size={16} />أضف إعلان</Link>}
          <UserArea onNav={() => setOpen(false)} />
        </div>
      )}
    </header>
  );
};
